import { fInventoryAdjust, fInventoryList } from "@/apis/factoryInventory";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { CommonListParams } from "@/types/common";
import { FactoryInventory as FactoryInventoryType } from "@/types/factoryInventory";
import { Button, Flex, Input, TableProps } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AddFacInventory, { AddType } from "./components/AddFacInventory";
import ChangeList from "./components/ChangeList";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

interface FactoryInventoryFilters {
  keyword: string;
}

const INITIAL_FACTORY_INVENTORY_FILTERS: FactoryInventoryFilters = {
  keyword: "",
};

const FactoryInventory: FC = () => {
  const tableLayoutRef = useRef<TableLayoutRef>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { t } = useTranslation();

  const [list, total, loading, { fetchList }] = useDataList<FactoryInventoryType, [CommonListParams]>({ fetchApi: fInventoryList });

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<FactoryInventoryFilters>(
    INITIAL_FACTORY_INVENTORY_FILTERS,
  );
  const { keyword } = draftFilters;
  const [addOpen, setAddOpen] = useState<AddType | undefined>(undefined);
  const [openList, setOpenList] = useState<FactoryInventoryType | null>(null);

  useEffect(() => {
    fetch(0, INITIAL_FACTORY_INVENTORY_FILTERS);
    return () => abortControllerRef.current?.abort();
  }, []);

  const fetch = (offset: number, filters: FactoryInventoryFilters) => {
    if (offset === 0) tableLayoutRef.current?.resetTablePage();
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchList({
      body: '',
      strs: filters.keyword ? filters.keyword.split(' ') : undefined,
      pagination: {
        offset,
        take: tableLayoutRef.current?.pageSize ?? DEFAULT_PAGE_SIZE,
      },
      controller
    })
  };

  const handleAddList = (data: FactoryInventoryType[]) => {
    // addItems([...data]);
    fetch(0, appliedFilters);
    setAddOpen(undefined);
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_FACTORY_INVENTORY_FILTERS);
    fetch(0, nextFilters);
  };

  const columns: TableProps<FactoryInventoryType>['columns'] = [
    {
      title: <SText text={t("product.code")} />,
      key: "productCode",
      width: 120,
      render: (_, record) => (
        <SText
          text={record.product?.code || ""}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: <SText text={t("product.name")} />,
      key: "productName",
      width: 150,
      render: (_, record) => (
        <SText
          text={record.product?.name || ""}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: <SText text={t("sale.prodMod")} />,
      key: "productModel",
      width: 220,
      render: (_, record) => (
        <SText
          text={record.product?.model || ""}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: <SText text={t("product.uom")} />,
      key: "uom",
      width: 80,
      align: "center",
      render: (_, record) => (
        <SText text={record.product?.uom || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("product.materialProcess")} />,
      key: "materialProcess",
      width: 80,
      render: (_, record) => (
        <SText text={record.product?.process || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("counting.quantity")} />,
      key: "count",
      width: 160,
      render: (_, record) => <SText text={record.count?.toString() || ""} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t("common.opt")} />,
      key: "opt",
      width: 160,
      render: (_, record) => <Button type="primary" onClick={() => setOpenList(record)}>{t('inventory.list')}</Button>
    },

  ];

  const headerChildren = (
    <Flex style={{ width: "100%" }} justify="space-between" >
      <Flex gap={8}>
        <Button type="primary" onClick={() => setAddOpen("ADD")} >{t('warehouse.inboundOprt')}</Button>
        <Button type="primary" onClick={() => setAddOpen("ADJUST")} >{t('warehouse.outboundOprt')}</Button>
        <Button type="primary" onClick={() => setAddOpen("COUNT")} >{t('counting.startInv')}</Button>
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={loading}
        >
          {({ submit }) => (
            <Input
              value={keyword}
              onChange={event =>
                updateDraftFilters({ keyword: event.target.value })
              }
              onPressEnter={submit}
              placeholder={t('order.searchPl')}
              style={{ width: 200 }}
            />
          )}
        </ListFilterBar>
      </div>
    </Flex>
  )
  return (
    <Flex vertical style={{ height: "100%" }}>
      <STableLayout
        columns={columns as Array<AnyObject>}
        tableLayoutRef={tableLayoutRef}
        dataSource={list}
        loading={loading}
        headerChildren={headerChildren}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
        scrollX={1200}
        extHeight={60}
      />
      {addOpen && <AddFacInventory open={addOpen} onClose={() => setAddOpen(undefined)} onSuccess={handleAddList} />}
      {openList && <ChangeList productId={openList.productId} onClose={() => setOpenList(null)} />}
    </Flex>
  )
};

export default FactoryInventory;
